import { BankOutlined, InfoCircleOutlined } from "@ant-design/icons";
import {
  Button,
  Form,
  Modal,
  Popconfirm,
  Space,
  Table,
  Tooltip,
  Typography,
} from "antd";
import denormalizeTaxNumber from "denormalize-steuernummer";
import { pick } from "lodash";
import moment from "moment";
import normalizeSteuernummer from "normalize-steuernummer";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";

import { TAX_ADVISORY_PERMISSION_SCOPES, useAuth } from "@/modules/auth";
import {
  useCreateTaxNumberMutation,
  useDeleteTaxNumberMutation,
  useUpdateTaxNumberMutation,
} from "../../../../../../api/graphql";
import {
  TAX_NUMBERS_QUERY,
  useTaxNumbersQuery,
} from "../../../../../../api/graphql/queries/taxNumbers";
import { TaxNumber } from "../../../../../../api/graphql/types";
import colors from "../../../../../../themes/colors";
import { IUser } from "../../../../../../types";
import {
  destroyMessage,
  showGraphQlErrorNotification,
  showLoadingMessage,
} from "../../../../../../utils";
import ActionLogDrawer from "../../../../../common/ActionLogDrawer";
import { mergeChangelogs } from "../../../../../common/ActionLogDrawer/helpers";
import Copyable from "../../../../../common/Copyable";
import {
  CustomerInformationCard,
  CustomerInformationSectionTitle,
} from "../../../../../common/styledComponents";
import { getGraphQlErrorType } from "../../../../../../utils/error";
import { notifySavedChange } from "../../utils";
import { VerticalAligned } from "../styledComponents";
import EditableRow from "./EditableRow";
import MissingTaxNumberTable from "./MissingTaxNumberTable";
import {
  IsMainBusinessTaxNumber,
  StateAbbreviation,
  TaxNumberType,
} from "./types";
import {
  cleanTaxNumber,
  getTaxAccountViewPath,
  getTaxNumberError,
  getTaxNumberErrorMessage,
  isTaxNumberWarning,
  NEW_TAX_NUMBER_RECORD_ID,
  STATE_LABEL,
  TABLE_COLUMN_TITLES,
  TAX_NUMBER_TYPE_LABELS,
} from "./utils";
import { DISPLAY_DATE_FORMAT } from "@/v1/constants";

const TAX_NUMBER_LOADING_KEY = "tax-number-loading";
const newRecordId: string = NEW_TAX_NUMBER_RECORD_ID;

const DUPLICATE_TAX_NUMBER_ERROR =
  "https://api.kontist.com/problems/duplicate_tax_number";
const INVALID_TAX_NUMBER_ERROR =
  "https://api.kontist.com/problems/invalid_tax_number";

const UserTaxNumbersList = ({ user }: { user: IUser }) => {
  const { t } = useTranslation("TaxNumbers");
  const { hasOperatorPermissionScope } = useAuth();
  const hasTaxAdvisoryPermissionScope = hasOperatorPermissionScope(
    TAX_ADVISORY_PERMISSION_SCOPES
  );

  const [form] = Form.useForm();
  const [editingKey, setEditingKey] = useState<string | null>(null);
  const [newTaxNumber, setNewTaxNumber] = useState<TaxNumber | null>(null);
  const [hasBusinessTaxNumber, setHasBusinessTaxNumber] =
    useState<boolean>(false);
  const [hasPersonalTaxNumber, setHasPersonalTaxNumber] =
    useState<boolean>(false);

  useEffect(() => {
    setHasBusinessTaxNumber(user.hasBusinessTaxNumber);
    setHasPersonalTaxNumber(user.hasPersonalTaxNumber);
  }, [user.hasBusinessTaxNumber, user.hasPersonalTaxNumber]);

  const { data: userTaxNumbersData } = useTaxNumbersQuery({
    variables: { email: user.email },
  });

  const [updateUserTaxNumber, { loading: updateLoading }] =
    useUpdateTaxNumberMutation({
      refetchQueries: [TAX_NUMBERS_QUERY],
    });

  const [createUserTaxNumber, { loading: createLoading }] =
    useCreateTaxNumberMutation({
      refetchQueries: [TAX_NUMBERS_QUERY],
    });

  const [deleteUserTaxNumber, { loading: deleteLoading }] =
    useDeleteTaxNumberMutation({
      refetchQueries: [TAX_NUMBERS_QUERY],
    });

  const isLoading = useMemo(
    () => updateLoading || createLoading || deleteLoading,
    [updateLoading, createLoading, deleteLoading]
  );

  const taxNumberDataSource = useMemo(() => {
    const taxNumbers = userTaxNumbersData?.taxNumbers || [];
    if (newTaxNumber) {
      return [...taxNumbers, newTaxNumber];
    }
    return taxNumbers;
  }, [userTaxNumbersData, newTaxNumber]);

  const isNewRecord = (id: string) => id === newRecordId;

  const confirmNoTaxOffice = useCallback(
    (content: string) =>
      new Promise<boolean>((resolve) => {
        Modal.confirm({
          title: t("noTaxOfficeConfirm.title"),
          content,
          okText: t("noTaxOfficeConfirm.saveAnyway"),
          cancelText: t("noTaxOfficeConfirm.cancel"),
          onOk: () => resolve(true),
          onCancel: () => resolve(false),
        });
      }),
    [t]
  );

  const save = useCallback(
    async (id: string) => {
      let row;
      try {
        // Blocks on FORMAT / WRONG_STATE via the rules on the tax number field.
        row = await form.validateFields();
      } catch {
        return;
      }

      if (isNewRecord(id)) {
        // The same cleaned value goes to the check and to normalize.
        const cleanedTaxNumber = cleanTaxNumber(row.rawTaxNumber);
        try {
          row.taxNumber = normalizeSteuernummer(cleanedTaxNumber, row.state);
        } catch {
          form.setFields([
            {
              name: "rawTaxNumber",
              errors: [
                getTaxNumberErrorMessage(
                  t,
                  getTaxNumberError(cleanedTaxNumber, row.state) ?? {
                    type: "FORMAT",
                  },
                  row.state
                ),
              ],
            },
          ]);
          return;
        }

        const taxNumberError = getTaxNumberError(cleanedTaxNumber, row.state);
        if (taxNumberError && isTaxNumberWarning(taxNumberError)) {
          const confirmed = await confirmNoTaxOffice(
            getTaxNumberErrorMessage(t, taxNumberError, row.state)
          );
          if (!confirmed) {
            return;
          }
        }
      }

      const payload = {
        ...pick(row, [
          "type",
          "taxNumber",
          "validFrom",
          "description",
          "isMainBusinessTaxNumber",
        ]),
      };

      try {
        showLoadingMessage(TAX_NUMBER_LOADING_KEY);
        if (isNewRecord(id)) {
          await createUserTaxNumber({
            variables: { email: user.email, payload },
          });
          setNewTaxNumber(null);
        } else {
          await updateUserTaxNumber({
            variables: { id, payload },
          });
        }
        row.type === TaxNumberType.PERSONAL
          ? setHasPersonalTaxNumber(true)
          : setHasBusinessTaxNumber(true);
        notifySavedChange();
        setEditingKey(null);
      } catch (error) {
        const errorType = getGraphQlErrorType(error);
        if (isNewRecord(id) && errorType === DUPLICATE_TAX_NUMBER_ERROR) {
          form.setFields([
            { name: "rawTaxNumber", errors: [t("errors.duplicate")] },
          ]);
        } else if (isNewRecord(id) && errorType === INVALID_TAX_NUMBER_ERROR) {
          form.setFields([
            {
              name: "rawTaxNumber",
              errors: [
                getTaxNumberErrorMessage(t, { type: "FORMAT" }, row.state),
              ],
            },
          ]);
        } else {
          showGraphQlErrorNotification(t("errors.saveFailed"), error);
        }
      } finally {
        destroyMessage(TAX_NUMBER_LOADING_KEY);
      }
    },
    [
      form,
      user.email,
      updateUserTaxNumber,
      createUserTaxNumber,
      confirmNoTaxOffice,
      t,
    ]
  );

  const deleteTaxNumberRecord = useCallback(
    async (id: string) => {
      if (id !== newRecordId) {
        try {
          showLoadingMessage(TAX_NUMBER_LOADING_KEY);
          await deleteUserTaxNumber({
            variables: { id },
          });
          notifySavedChange();
        } catch (error) {
          showGraphQlErrorNotification(t("errors.deleteFailed"), error);
        } finally {
          destroyMessage(TAX_NUMBER_LOADING_KEY);
        }
      } else {
        setNewTaxNumber(null);
      }
      setEditingKey(null);
    },
    [deleteUserTaxNumber, t]
  );

  const add = useCallback(() => {
    form.resetFields();
    setNewTaxNumber({
      id: newRecordId,
      type: TaxNumberType.PERSONAL,
      description: "",
      isMainBusinessTaxNumber: false,
      taxNumber: "",
    });
    setEditingKey(newRecordId);
  }, [form]);

  const edit = useCallback(
    (record: TaxNumber) => {
      form.resetFields();
      setEditingKey(record.id);
    },
    [form]
  );

  const cancel = useCallback(() => {
    setEditingKey(null);
    if (newTaxNumber) {
      setNewTaxNumber(null);
    }
  }, [newTaxNumber]);

  const isEditing = useCallback(
    (record: TaxNumber) => record.id === editingKey,
    [editingKey]
  );

  const changeLogs = useMemo(
    () =>
      mergeChangelogs(
        userTaxNumbersData?.taxNumbers?.map((tax) => {
          const { taxNumber } = denormalizeTaxNumber(tax.taxNumber) || {};

          return {
            ...tax,
            ...(taxNumber
              ? { subText: t("changeLogTaxNumber", { taxNumber }) }
              : {}),
          };
        }) || []
      ),
    [userTaxNumbersData, t]
  );

  const columns = useMemo(
    () => [
      {
        key: "type",
        title: TABLE_COLUMN_TITLES.TYPE,
        dataIndex: "type",
        editable: true,
        required: true,
        width: "180px",
        render: (type: TaxNumberType) => TAX_NUMBER_TYPE_LABELS[type],
      },
      {
        key: "state",
        title: TABLE_COLUMN_TITLES.STATE,
        width: "150px",
        // The update mutation cannot carry the state, so it is only editable
        // for the new row (like the tax number).
        editable: editingKey === newRecordId,
        required: true,
        render: (record: TaxNumber) => {
          const label =
            STATE_LABEL[
              denormalizeTaxNumber(record.taxNumber)
                ?.taxState as StateAbbreviation
            ];
          return isEditing(record) && !isNewRecord(record.id) ? (
            <Tooltip title={t("stateLocked")}>
              {label} <InfoCircleOutlined />
            </Tooltip>
          ) : (
            label
          );
        },
      },
      {
        key: "rawTaxNumber",
        title: TABLE_COLUMN_TITLES.TAX_NUMBER,
        width: "170px",
        editable: editingKey === newRecordId,
        required: true,
        render: (record: TaxNumber) => (
          <Copyable withFixedIcon>
            {denormalizeTaxNumber(record.taxNumber)?.taxNumber}
          </Copyable>
        ),
      },
      {
        key: "taxAccountView",
        title: "",
        render: (record: TaxNumber) => (
          <>
            <Link
              to={getTaxAccountViewPath({
                email: user.email,
                taxNumber: record.taxNumber,
              })}
            >
              <BankOutlined style={{ color: colors.purple }} />
            </Link>
          </>
        ),
      },
      {
        key: "validFrom",
        title: TABLE_COLUMN_TITLES.VALID_FROM,
        editable: true,
        width: "100px",
        required: false,
        render: (record: TaxNumber) =>
          record.validFrom &&
          moment(record.validFrom).format(DISPLAY_DATE_FORMAT),
      },
      {
        key: "description",
        title: TABLE_COLUMN_TITLES.DESCRIPTION,
        dataIndex: "description",
        width: "150px",
        editable: true,
        required: false,
      },
      {
        key: "isMainBusinessTaxNumber",
        title: TABLE_COLUMN_TITLES.MAIN_TAX_NUMBER,
        dataIndex: "isMainBusinessTaxNumber",
        editable: true,
        required: true,
        width: "180px",
        render: (isMainBusinessTaxNumber: boolean) => {
          if (isMainBusinessTaxNumber) {
            return IsMainBusinessTaxNumber.YES;
          }
          return IsMainBusinessTaxNumber.NO;
        },
      },
      {
        key: "action",
        title: TABLE_COLUMN_TITLES.ACTION,
        render: (_: any, record: TaxNumber) => {
          if (record.deletedAt) {
            return t("actions.deleted");
          }
          const editable = isEditing(record);
          return editable ? (
            <span>
              <Typography.Link
                onClick={() => save(record.id)}
                style={{ marginRight: 8 }}
                disabled={isLoading}
              >
                {t("actions.save")}
              </Typography.Link>
              <Typography.Link
                onClick={cancel}
                style={{ marginRight: 8 }}
                disabled={isLoading}
              >
                {t("actions.cancel")}
              </Typography.Link>
              <Popconfirm
                title={t("actions.confirmDelete")}
                onConfirm={() => deleteTaxNumberRecord(record.id)}
              >
                <Typography.Link
                  style={{ marginRight: 8 }}
                  disabled={isLoading}
                >
                  {t("actions.delete")}
                </Typography.Link>
              </Popconfirm>
            </span>
          ) : (
            <Typography.Link
              disabled={editingKey !== null}
              onClick={() => edit(record)}
            >
              {t("actions.edit")}
            </Typography.Link>
          );
        },
      },
    ],
    [
      cancel,
      deleteTaxNumberRecord,
      edit,
      editingKey,
      isEditing,
      isLoading,
      save,
      t,
      user.email,
    ]
  );

  const mergedColumns = useMemo(
    () =>
      columns.map((col) => {
        if (!col.editable) {
          return col;
        }

        return {
          ...col,
          onCell: (record: TaxNumber) => ({
            record,
            dataIndex: col.key,
            title: col.title,
            editing: isEditing(record),
            required: col.required,
          }),
        };
      }),
    [columns, isEditing]
  );

  return (
    <Form form={form} component={false}>
      <CustomerInformationSectionTitle>
        <Space>
          {t("heading")}
          <ActionLogDrawer title={t("actionLog")} changeLogs={changeLogs} />
        </Space>
      </CustomerInformationSectionTitle>
      <CustomerInformationCard>
        <VerticalAligned>
          <Table
            rowClassName={(record) =>
              record.deletedAt ? "ant-typography ant-typography-disabled" : ""
            }
            components={{
              body: {
                cell: EditableRow,
              },
            }}
            dataSource={taxNumberDataSource}
            columns={mergedColumns}
            pagination={false}
          />
          {hasTaxAdvisoryPermissionScope && (
            <div style={{ display: "flex", justifyContent: "end" }}>
              <Button disabled={editingKey !== null} onClick={add}>
                {t("actions.add")}
              </Button>
            </div>
          )}
        </VerticalAligned>
      </CustomerInformationCard>
      <CustomerInformationCard>
        <MissingTaxNumberTable
          missingBusinessTaxNumberNote={user.missingBusinessTaxNumberNote}
          missingPersonalTaxNumberNote={user.missingPersonalTaxNumberNote}
          hasBusinessTaxNumber={hasBusinessTaxNumber}
          hasPersonalTaxNumber={hasPersonalTaxNumber}
        ></MissingTaxNumberTable>
      </CustomerInformationCard>
    </Form>
  );
};

export default UserTaxNumbersList;
